import { useEffect, useRef, useState } from "react";
import { Users as UsersIcon, Check, X } from "lucide-react";
import api from "../../lib/api";

/**
 * <TaskAssigneePicker /> — Iter 349 (2026-02-26).
 *
 * Dirk: "add a small drop down on each jc task to selct which team
 * member in site must carry it out. Anyone can edit, but it must show
 * who appointed the member … Allow multiple asignees per task. If no
 * team member is selected, anyone can do it at it must be on
 * everyones 'my tasks'. The membet must be displayd effectively and
 * clicking on thr name can open dropdown so we don't need more
 * buttons? Be very careful and wise to not clutter thr screen".
 *
 * Contract:
 *   - Empty selection renders a subtle "Anyone" pill. Clicking any pill
 *     (including "Anyone") opens the dropdown — no extra button needed.
 *   - Multi-select: any number of members can be assigned. Checkbox
 *     toggle saves immediately via `onChange`.
 *   - Team pool is derived from the JC (primary technician + staff_ids);
 *     falls back to `extraUsers` (usually the tenant's admins/managers)
 *     so an owner can reassign to someone not yet on the JC's team.
 *   - The "appointed by …" byline shows below the pills when the
 *     server has stamped `assigneeEditBy`. Tiny, muted, no icon —
 *     accountability without noise.
 *
 * Props:
 *   selected: string[]                 — user ids currently assigned
 *   selectedNames?: string[]           — cached names (falls back to `users` lookup)
 *   teamUserIds: string[]              — JC's core team (tech + staff)
 *   extraUsers?: {id, name, role}[]    — additional pickable users (managers)
 *   users: {id, name, role}[]          — full lookup map for name resolution
 *   canEdit: boolean                   — flip to read-only for e.g. clients
 *   appointedBy?: {name, at} | null    — display "appointed by X · ago"
 *   onChange: (nextIds: string[]) => Promise<void> | void
 */
export default function TaskAssigneePicker({
  selected = [],
  selectedNames = [],
  teamUserIds = [],
  extraUsers = [],
  users = [],
  canEdit = true,
  appointedBy = null,
  onChange,
  testId = "task-assignee",
}) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const wrapRef = useRef(null);
  const [live, setLive] = useState(selected);

  useEffect(() => setLive(selected), [selected]);

  // Close on outside click. Iter 349 — we render inline (not a portal)
  // so a plain "click-outside" listener is enough.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
    };
  }, [open]);

  const userMap = new Map();
  users.forEach((u) => u && userMap.set(u.id, u));
  extraUsers.forEach((u) => u && !userMap.has(u.id) && userMap.set(u.id, u));

  const nameFor = (uid, i) =>
    userMap.get(uid)?.name || selectedNames[i] || uid.slice(0, 8);

  const initials = (name) =>
    (name || "?")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() || "")
      .join("") || "?";

  // Candidate pool = JC team ∪ extras. De-dupe.
  const seen = new Set();
  const candidates = [];
  for (const uid of [...teamUserIds, ...extraUsers.map((u) => u.id)]) {
    if (!uid || seen.has(uid)) continue;
    seen.add(uid);
    const u = userMap.get(uid);
    if (u) candidates.push(u);
  }
  // Always include already-selected users so they render at the top
  // even if they've since been removed from the JC's core team.
  for (const uid of selected) {
    if (!seen.has(uid)) {
      seen.add(uid);
      candidates.unshift(userMap.get(uid) || { id: uid, name: uid.slice(0, 8) });
    }
  }

  const toggle = async (uid) => {
    const next = live.includes(uid)
      ? live.filter((x) => x !== uid)
      : [...live, uid];
    setLive(next);
    setSaving(true);
    try {
      await onChange?.(next);
    } finally {
      setSaving(false);
    }
  };

  const clearAll = async () => {
    setLive([]);
    setSaving(true);
    try {
      await onChange?.([]);
    } finally {
      setSaving(false);
    }
  };

  const relTime = (iso) => {
    if (!iso) return "";
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86_400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86_400)}d ago`;
  };

  return (
    <div className="relative inline-block" ref={wrapRef} data-testid={testId}>
      <button
        type="button"
        onClick={() => canEdit && setOpen((o) => !o)}
        disabled={!canEdit}
        className={`inline-flex items-center gap-1.5 max-w-full px-2 py-1 rounded-md text-[11px] leading-tight transition border ${
          live.length > 0
            ? "border-[#1FB6C1]/40 bg-[#1FB6C1]/10 text-[#a0e6ea] hover:bg-[#1FB6C1]/15"
            : "border-white/10 bg-white/[0.03] text-[#8d94a8] hover:text-white hover:border-white/20"
        } ${!canEdit ? "opacity-70 cursor-not-allowed" : ""}`}
        title={
          live.length > 0
            ? `Assigned to ${live.map(nameFor).join(", ")}`
            : "Open task — anyone can do it"
        }
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid={`${testId}-trigger`}
      >
        <UsersIcon className="w-3 h-3 shrink-0" />
        {live.length === 0 ? (
          <span>Anyone</span>
        ) : (
          <span className="inline-flex items-center gap-1 min-w-0">
            {live.slice(0, 2).map((uid, i) => (
              <span
                key={uid}
                className="inline-flex items-center gap-1 max-w-[9rem]"
              >
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#1FB6C1]/30 text-[9px] font-bold tabular-nums shrink-0">
                  {initials(nameFor(uid, i))}
                </span>
                <span className="truncate">{nameFor(uid, i)}</span>
              </span>
            ))}
            {live.length > 2 && (
              <span className="ml-0.5 text-[10px] opacity-70">
                +{live.length - 2}
              </span>
            )}
          </span>
        )}
      </button>

      {appointedBy?.name && live.length > 0 && (
        <div className="mt-0.5 text-[9px] text-[#647087] leading-none">
          by {appointedBy.name} · {relTime(appointedBy.at)}
        </div>
      )}

      {open && (
        <div
          className="absolute z-30 mt-1 right-0 w-64 rounded-md border border-white/10 bg-[#0f1116] shadow-xl overflow-hidden"
          role="listbox"
          data-testid={`${testId}-menu`}
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-white/5">
            <span className="text-[10px] uppercase tracking-wider text-[#647087]">
              Assign to
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-[#647087] hover:text-white"
              aria-label="Close picker"
              data-testid={`${testId}-close`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {candidates.length === 0 && (
              <div className="px-3 py-2 text-xs text-[#647087]">
                No teammates on this JC yet.
              </div>
            )}
            {candidates.map((u) => {
              const on = live.includes(u.id);
              return (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => toggle(u.id)}
                  disabled={saving}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-white/5 disabled:opacity-60 ${
                    on ? "text-white" : "text-[#c6ccd7]"
                  }`}
                  data-testid={`${testId}-opt-${u.id}`}
                >
                  <span
                    className={`inline-flex items-center justify-center w-4 h-4 rounded border ${
                      on
                        ? "bg-[#1FB6C1] border-[#1FB6C1] text-black"
                        : "border-white/20"
                    }`}
                  >
                    {on && <Check className="w-3 h-3" />}
                  </span>
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#1FB6C1]/20 text-[10px] font-bold shrink-0">
                    {initials(u.name)}
                  </span>
                  <span className="flex-1 truncate">{u.name || u.id.slice(0, 8)}</span>
                  {u.role && (
                    <span className="text-[9px] text-[#647087] uppercase tracking-wider">
                      {u.role}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <div className="border-t border-white/5 px-3 py-1.5 flex items-center justify-between">
            <button
              type="button"
              onClick={clearAll}
              disabled={live.length === 0 || saving}
              className="text-[10px] text-[#8d94a8] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid={`${testId}-clear`}
            >
              Clear (open to anyone)
            </button>
            <span className="text-[9px] text-[#647087]">
              {live.length} selected
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Utility — decide whether a task should appear in a given user's
 * "My tasks" filter. Rule (per the Iter 349 spec):
 *   • Task with no assignees → open, visible to everyone.
 *   • Task with assignees → visible only to those specific users.
 */
export function taskInMyList(task, userId) {
  const ids = task?.assignee_ids || [];
  if (!ids.length) return true;
  return ids.includes(userId);
}
